/**
 * @description 使用者 Profile Dropdown
 * @property {String} name - 使用者名稱
 * @property {String} ipadPosition - 平板彈窗位置
 * @property {Function} logout - 登出函式
 * @property {Function} getResetPwdCode - 獲取重置密碼碼函式
 * 範例:
 * const getResetPasswordCodeAPI = () => {
 *    return axios.get(`${LAB_HOST}/member/renew_password/hash_value`)
 * }
 */

import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { profileLinks } from '../../config/system';
import { uris } from '../../config/environment';
import getUrl from '../utils/getUrl';
import './ls-dropdown-button';
import './ls-modal';
import { type ResetPasswordCodeResponse } from '../types/api';
import { type RoleInfo } from '../types/leishan';

@customElement('ls-profile-dropdown')
export class LsProfileDropdown extends LitElement {
    static styles = css`
        .ls-profile-dropdown {
            position: relative;
            width: fit-content;
        }

        ls-modal {
            display: flex;
            flex-direction: column;
        }

        .profile-link {
            box-sizing: border-box;
            display: block;
            text-decoration: none;
            width: 100%;
            height: 40px;
            color: #333;
            font-weight: 500;
            font-size: 14px;
            line-height: 17px;
            border-radius: 5px;
            padding: 11.5px 16px;
        }

        .profile-link:last-of-type {
            border-bottom: 1px solid #dcdfe4;
        }

        .profile-link:hover {
            background-color: #00adee1a;
            color: #333;
        }

        .logout-button {
            display: flex;
            align-items: center;
            justify-content: space-between;
            width: 100%;
            padding: 16.5px 16px 11.5px;
            margin-top: 5 px;

            gap: 10px;
            background-color: transparent;
            cursor: pointer;
            font-weight: 500;
            font-size: 14px;
            line-height: 17px;

            border: none;
        }
    `;

    @property({ type: String }) name: string = '';
    @property({ type: Function }) logout: () => void = () => {};
    @property({ type: String }) ipadPosition: 'origin' | 'center' = 'center';
    @property({ type: Function }) getResetPwdCode: () => Promise<ResetPasswordCodeResponse> = () =>
        Promise.resolve({} as ResetPasswordCodeResponse);
    @property({ type: Object }) roleInfo: RoleInfo = {};

    @state() isActive: boolean = false;
    @state() isClicking: boolean = false;

    private _handleDropdownButtonClick(event: Event) {
        this.isClicking = true;
        this.isActive = !this.isActive;
        setTimeout(() => (this.isClicking = false), 0);
    }

    connectedCallback(): void {
        super.connectedCallback();
        const self = this as HTMLElement;
        document.addEventListener(
            'click',
            (event) => {
                if (this.isClicking) return;
                if (!(event.target instanceof HTMLElement)) return;
                if (self.contains(event.target)) return;
                this.isActive = false;
            },
            { capture: false },
        );
    }

    private async _goResetPassword(uri: string, e: Event) {
        if (uri) return;
        e.preventDefault();
        try {
            const res = await this.getResetPwdCode();
            const hashValue = res.data?.hashValue || (res as any)?.hashValue;
            window.location.href = `${uris.SSO_URL}reset_password?code=${hashValue}`;
        } catch (e) {
            console.log(e);
        }
    }

    render() {
        const logoutIconUrl = getUrl(`assets/images/logout-icon.webp`);

        return html`<div class="ls-profile-dropdown">
            <ls-dropdown-button .isActive=${this.isActive} @click=${this._handleDropdownButtonClick}>
                <div>${this.name}</div>
            </ls-dropdown-button>
            ${this.isActive
                ? html`<ls-modal
                      align="right"
                      .onClose=${() => (this.isActive = false)}
                      ipadPosition="${this.ipadPosition}"
                  >
                      ${profileLinks.map((link) => {
                          if (link.permission && !link.permission(this.roleInfo)) return;
                          return html`<a
                              class="profile-link"
                              href=${link.uri}
                              target="_self"
                              @click=${(e: Event) => this._goResetPassword(link.uri, e)}
                              >${link.name}</a
                          >`;
                      })}
                      <button class="logout-button" @click=${this.logout}>
                          <span>登出</span>
                          <img src=${logoutIconUrl} width="16" height="20" alt="logout-icon" />
                      </button>
                  </ls-modal>`
                : ''}
        </div>`;
    }
}
